import React, { useEffect, useState } from 'react';
import { adminApi } from './adminApi';
import { Inbox, Users, FileText, Briefcase, ArrowUpRight, Bell } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from './AuthContext';

const StatCard = ({ icon: Icon, label, value, accent, to, testId, hint }) => {
  const Wrap = to ? Link : 'div';
  const wrapProps = to ? { to } : {};
  return (
    <Wrap
      {...wrapProps}
      data-testid={testId}
      className="group block bg-[#15151A] border border-white/5 rounded-2xl p-6 hover:border-[#83BF42]/30 transition-colors"
    >
      <div className="flex items-start justify-between mb-6">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${accent}`}>
          <Icon className="w-5 h-5" />
        </div>
        {to && (
          <ArrowUpRight className="w-4 h-4 text-white/30 group-hover:text-[#83BF42] transition-colors" />
        )}
      </div>
      <p className="text-white text-3xl font-light font-['Inter']">{value}</p>
      <p className="text-white/50 text-xs uppercase tracking-wider font-['Inter'] mt-2">{label}</p>
      {hint && <p className="text-[#83BF42] text-xs mt-2 font-['Lato']">{hint}</p>}
    </Wrap>
  );
};

const AdminDashboard = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Promise.allSettled so a single role-gated 403 (e.g. listContacts for editors)
    // doesn't crash the dashboard. Stats are visible to all roles; the recent-leads
    // section is admin-only and gracefully hides for editors.
    Promise.allSettled([adminApi.stats(), adminApi.listContacts()])
      .then(([statsResult, contactsResult]) => {
        if (statsResult.status === 'fulfilled') setStats(statsResult.value);
        if (contactsResult.status === 'fulfilled') {
          setRecent((contactsResult.value || []).slice(0, 5));
        }
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="px-8 lg:px-12 py-10" data-testid="admin-dashboard-page">
      <div data-testid="admin-dashboard">
      <header className="mb-10">
        <p className="text-[#83BF42] text-xs uppercase tracking-[0.2em] font-['Inter'] mb-2">
          Overview
        </p>
        <h1 className="text-white text-4xl font-light font-['Inter']">Dashboard</h1>
        <p className="text-white/40 text-sm font-['Lato'] mt-2">
          Pipeline, audience and content at a glance.
        </p>
      </header>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-2 border-[#83BF42] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
            {isAdmin && (
              <StatCard
                icon={Inbox}
                label="Total Leads"
                value={stats?.contacts_total ?? 0}
                accent="bg-[#83BF42]/10 text-[#83BF42]"
                to="/admin/contacts"
                testId="stat-contacts-total"
                hint={stats?.contacts_new ? `${stats.contacts_new} new to review` : null}
              />
            )}
            {isAdmin && (
              <StatCard
                icon={Users}
                label="Newsletter Subscribers"
                value={stats?.subscribers_total ?? 0}
                accent="bg-blue-500/10 text-blue-400"
                to="/admin/subscribers"
                testId="stat-subscribers-total"
              />
            )}
            <StatCard
              icon={FileText}
              label="Published Articles"
              value={stats?.articles_total ?? 0}
              accent="bg-purple-500/10 text-purple-400"
              to="/admin/articles"
              testId="stat-articles-total"
            />
            {isAdmin && (
              <StatCard
                icon={Briefcase}
                label="Case Studies"
                value={stats?.case_studies_total ?? 0}
                accent="bg-orange-500/10 text-orange-400"
                testId="stat-cases-total"
              />
            )}
          </div>

          {/* Recent leads — admin only (editors don't have CRM access) */}
          {isAdmin && (
          <section className="bg-[#15151A] border border-white/5 rounded-2xl p-6 lg:p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-white text-lg font-medium font-['Inter'] flex items-center gap-2">
                <Bell className="w-4 h-4 text-[#83BF42]" />
                Recent Leads
              </h2>
              <Link
                to="/admin/contacts"
                data-testid="dashboard-view-all-leads"
                className="text-[#83BF42] text-xs font-['Inter'] hover:underline"
              >
                View all →
              </Link>
            </div>
            {recent.length === 0 ? (
              <p className="text-white/40 text-sm font-['Lato'] py-8 text-center">
                No leads yet. They'll appear here when contact form is submitted.
              </p>
            ) : (
              <ul className="divide-y divide-white/5">
                {recent.map((c) => (
                  <li key={c.id} className="py-3 flex items-center justify-between gap-4" data-testid={`dashboard-lead-${c.id}`}>
                    <div className="min-w-0">
                      <p className="text-white text-sm font-['Inter'] truncate">
                        {c.firstName} {c.lastName} · <span className="text-white/40">{c.company}</span>
                      </p>
                      <p className="text-white/40 text-xs font-['Lato'] truncate">{c.email}</p>
                    </div>
                    <StatusPill status={c.status} />
                  </li>
                ))}
              </ul>
            )}
          </section>
          )}

          {/* Editor welcome card — replaces the leads panel for editor role */}
          {!isAdmin && (
            <section
              data-testid="dashboard-editor-welcome"
              className="bg-[#15151A] border border-white/5 rounded-2xl p-6 lg:p-8"
            >
              <h2 className="text-white text-lg font-medium font-['Inter'] flex items-center gap-2">
                <FileText className="w-4 h-4 text-[#83BF42]" />
                Editor workspace
              </h2>
              <p className="text-white/50 text-sm font-['Lato'] font-light mt-3 max-w-xl">
                You&rsquo;re signed in as an editor. Head to the Blog Editor to publish, edit and schedule
                articles. If you need access to leads, subscribers, broadcasts or settings, ask an admin to
                upgrade your role.
              </p>
              <div className="mt-5 flex flex-wrap gap-3">
                <Link
                  to="/admin/articles"
                  data-testid="dashboard-go-to-blog"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#83BF42] text-[#242326] text-sm font-['Inter'] font-medium hover:bg-[#76ad3a] transition"
                >
                  Open Blog Editor →
                </Link>
                <Link
                  to="/admin/settings"
                  data-testid="dashboard-go-to-settings"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/10 text-white/80 text-sm font-['Inter'] hover:bg-white/5 transition"
                >
                  Change my password
                </Link>
              </div>
            </section>
          )}
        </>
      )}
      </div>
    </div>
  );
};

export const STATUS_META = {
  new: { label: 'New', color: 'bg-[#83BF42]/15 text-[#83BF42] border-[#83BF42]/30' },
  contacted: { label: 'Contacted', color: 'bg-blue-500/15 text-blue-300 border-blue-500/30' },
  qualified: { label: 'Qualified', color: 'bg-purple-500/15 text-purple-300 border-purple-500/30' },
  won: { label: 'Won', color: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
  lost: { label: 'Lost', color: 'bg-red-500/15 text-red-300 border-red-500/30' },
};

export const TIER_META = {
  hot: { label: 'HOT', color: 'bg-red-500/15 text-red-300 border-red-500/40' },
  warm: { label: 'WARM', color: 'bg-orange-500/15 text-orange-300 border-orange-500/40' },
  cool: { label: 'COOL', color: 'bg-blue-500/15 text-blue-300 border-blue-500/30' },
  cold: { label: 'COLD', color: 'bg-white/5 text-white/40 border-white/10' },
};

export const StatusPill = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.new;
  return (
    <span
      className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-['Inter'] border ${meta.color}`}
      data-testid={`status-pill-${status}`}
    >
      {meta.label}
    </span>
  );
};

export const TierPill = ({ tier, score }) => {
  const meta = TIER_META[tier] || TIER_META.cold;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[10px] font-['Inter'] border ${meta.color}`}
      data-testid={`tier-pill-${tier}`}
      title={typeof score === 'number' ? `Score ${score}/100` : ''}
    >
      <span>{meta.label}</span>
      {typeof score === 'number' && <span className="opacity-60">{score}</span>}
    </span>
  );
};

export default AdminDashboard;
